import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { STATUS_PIN_COLORS } from '../models/Client';
import { loadLeaflet } from '../utils/loadLeaflet';

const POLAND_CENTER = [52.07, 19.48];
const DEFAULT_ZOOM = 6;
const MARKER_BATCH_SIZE = 100;
const VIEWPORT_CULLING_MIN_MARKERS = 80;

const isValidCoordinate = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng);

const pinIconCache = new Map();

const createPinIcon = (L, color) =>
  L.divIcon({
    className: 'crm-map-pin',
    html: `<span style="
      display:block;
      width:16px;
      height:16px;
      border-radius:50%;
      background:${color};
      border:2px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,0.35);
    "></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const getPinIcon = (L, color) => {
  if (!pinIconCache.has(color)) {
    pinIconCache.set(color, createPinIcon(L, color));
  }
  return pinIconCache.get(color);
};

const ClientsLeafletMap = ({ locations, onClientSelect }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const markersByIdRef = useRef(new Map());
  const validLocationsRef = useRef([]);
  const tooltipRef = useRef(null);
  const onClientSelectRef = useRef(onClientSelect);
  const [leafletReady, setLeafletReady] = useState(Boolean(window.L));

  const validLocations = useMemo(
    () => locations.filter((location) => isValidCoordinate(location.lat, location.lng)),
    [locations]
  );

  const locationsKey = useMemo(
    () => validLocations.map((location) => `${location.id}:${location.lat}:${location.lng}:${location.status}`).join('|'),
    [validLocations]
  );

  useEffect(() => {
    validLocationsRef.current = validLocations;
  }, [validLocations]);

  useEffect(() => {
    onClientSelectRef.current = onClientSelect;
  }, [onClientSelect]);

  useEffect(() => {
    let cancelled = false;

    loadLeaflet()
      .then(() => {
        if (!cancelled) setLeafletReady(true);
      })
      .catch(() => {
        if (!cancelled) setLeafletReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!leafletReady || !containerRef.current || !window.L) return undefined;

    let cancelled = false;
    let frameId = null;
    let batchIndex = 0;
    let syncTimeoutId = null;
    const L = window.L;

    const hideTooltip = () => {
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
      }
    };

    const clearMarkers = () => {
      const markersLayer = markersLayerRef.current;
      if (!markersLayer) return;

      hideTooltip();

      markersByIdRef.current.forEach(({ marker }) => {
        markersLayer.removeLayer(marker);
      });
      markersByIdRef.current.clear();
    };

    const ensureTooltip = () => {
      if (tooltipRef.current || !mapRef.current) return tooltipRef.current;

      const tooltip = document.createElement('div');
      tooltip.className = 'crm-map-hover-tooltip';
      tooltip.setAttribute('role', 'tooltip');
      mapRef.current.getContainer().appendChild(tooltip);
      tooltipRef.current = tooltip;
      return tooltip;
    };

    const showTooltip = (location) => {
      const map = mapRef.current;
      const tooltip = ensureTooltip();
      if (!map || !tooltip) return;

      const point = map.latLngToContainerPoint([location.lat, location.lng]);
      tooltip.textContent = location.companyName || '—';
      tooltip.style.left = `${point.x}px`;
      tooltip.style.top = `${point.y}px`;
      tooltip.style.display = 'block';
    };

    const attachHoverHandlers = (marker, location) => {
      marker.on('mouseover', () => showTooltip(location));
      marker.on('mouseout', hideTooltip);
    };

    const createMarker = (location) => {
      const color = STATUS_PIN_COLORS[location.status] || '#1976d2';
      const marker = L.marker([location.lat, location.lng], {
        icon: getPinIcon(L, color),
      });

      marker.on('click', () => onClientSelectRef.current(location.id));
      attachHoverHandlers(marker, location);
      return marker;
    };

    const shouldUseViewportCulling = () =>
      validLocationsRef.current.length >= VIEWPORT_CULLING_MIN_MARKERS;

    const getVisibleLocations = () => {
      const currentLocations = validLocationsRef.current;
      if (!shouldUseViewportCulling() || !mapRef.current) {
        return currentLocations;
      }

      const bounds = mapRef.current.getBounds().pad(0.12);
      return currentLocations.filter((location) => bounds.contains([location.lat, location.lng]));
    };

    const syncMarkers = () => {
      if (cancelled || !markersLayerRef.current) return;

      const markersLayer = markersLayerRef.current;
      const visibleLocations = getVisibleLocations();
      const visibleIds = new Set(visibleLocations.map((location) => location.id));

      visibleLocations.forEach((location) => {
        const existing = markersByIdRef.current.get(location.id);

        if (existing?.status === location.status) {
          return;
        }

        if (existing) {
          markersLayer.removeLayer(existing.marker);
        }

        const marker = createMarker(location);
        marker.addTo(markersLayer);
        markersByIdRef.current.set(location.id, {
          marker,
          status: location.status,
        });
      });

      markersByIdRef.current.forEach(({ marker }, id) => {
        if (!visibleIds.has(id)) {
          markersLayer.removeLayer(marker);
          markersByIdRef.current.delete(id);
        }
      });
    };

    const addMarkerBatch = () => {
      if (cancelled || !markersLayerRef.current) return;

      const visibleLocations = getVisibleLocations();
      const batch = visibleLocations.slice(batchIndex, batchIndex + MARKER_BATCH_SIZE);
      batchIndex += MARKER_BATCH_SIZE;

      const markersLayer = markersLayerRef.current;
      batch.forEach((location) => {
        const existing = markersByIdRef.current.get(location.id);
        if (existing?.status === location.status) return;

        if (existing) {
          markersLayer.removeLayer(existing.marker);
        }

        const marker = createMarker(location);
        marker.addTo(markersLayer);
        markersByIdRef.current.set(location.id, {
          marker,
          status: location.status,
        });
      });

      if (batchIndex < visibleLocations.length) {
        frameId = window.requestAnimationFrame(addMarkerBatch);
      }
    };

    const scheduleMarkerSync = () => {
      if (syncTimeoutId !== null) {
        window.clearTimeout(syncTimeoutId);
      }
      syncTimeoutId = window.setTimeout(() => {
        syncTimeoutId = null;
        if (!cancelled) {
          syncMarkers();
        }
      }, 120);
    };

    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, {
        center: POLAND_CENTER,
        zoom: DEFAULT_ZOOM,
        scrollWheelZoom: true,
        preferCanvas: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
        updateWhenIdle: true,
        keepBuffer: 4,
      }).addTo(mapRef.current);

      markersLayerRef.current = L.layerGroup().addTo(mapRef.current);
      mapRef.current.on('movestart zoomstart', hideTooltip);
      mapRef.current.on('moveend zoomend', scheduleMarkerSync);
    }

    clearMarkers();
    batchIndex = 0;

    const startMarkerBatch = () => {
      if (validLocationsRef.current.length) {
        frameId = window.requestAnimationFrame(addMarkerBatch);
      }
    };

    const timeoutId = window.setTimeout(() => {
      if (cancelled || !mapRef.current) return;

      const currentMap = mapRef.current;
      const currentLocations = validLocationsRef.current;

      if (!currentLocations.length) {
        currentMap.setView(POLAND_CENTER, DEFAULT_ZOOM);
      } else if (currentLocations.length === 1) {
        const location = currentLocations[0];
        currentMap.setView([location.lat, location.lng], 14);
      } else {
        const bounds = L.latLngBounds(currentLocations.map((location) => [location.lat, location.lng]));
        currentMap.fitBounds(bounds, { padding: [32, 32], maxZoom: 12 });
      }

      currentMap.invalidateSize();
      startMarkerBatch();
    }, 0);

    return () => {
      cancelled = true;
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      if (syncTimeoutId !== null) {
        window.clearTimeout(syncTimeoutId);
      }
      window.clearTimeout(timeoutId);
    };
  }, [leafletReady, locationsKey]);

  useEffect(
    () => () => {
      if (mapRef.current) {
        mapRef.current.off('movestart zoomstart');
        mapRef.current.off('moveend zoomend');
        mapRef.current.remove();
        mapRef.current = null;
        markersLayerRef.current = null;
        markersByIdRef.current.clear();
      }
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    },
    []
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 280,
        borderRadius: 1,
        overflow: 'hidden',
        '& .leaflet-container': {
          width: '100%',
          height: '100%',
          minHeight: 280,
          fontFamily: 'inherit',
        },
        '& .crm-map-pin': {
          background: 'transparent',
          border: 'none',
        },
        '& .crm-map-hover-tooltip': {
          position: 'absolute',
          display: 'none',
          pointerEvents: 'none',
          zIndex: 1000,
          transform: 'translate(-50%, calc(-100% - 10px))',
          maxWidth: 220,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          bgcolor: 'rgba(33, 33, 33, 0.92)',
          color: '#fff',
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
        },
      }}
    />
  );
};

export default ClientsLeafletMap;
